import { HStack, VStack } from "@chakra-ui/react";
import { number, object } from "decoders";
import { useParams } from "react-router-dom";
import { P, match } from "ts-pattern";
import { usePrintData } from "../../services/projectService";
import "../../styles/printing.css";
import { PrintDashboardData, PrintData, PrintWellLevelsData } from "../../types/printData";
import { ProjectID } from "../../types/project";
import { wellLevelsSitesReference } from "../../types/wellLevelsData";
import { PrintControls } from "../PrintControls/PrintControls";

export function PrintPage() {
  const { id } = useParams<{ id: string }>();

  if (!id) return <ErrorDisplay error="Invalid ID" />;

  return <PrintDataDisplay id={id} />;
}

export function PrintDataDisplay({ id }: { id: string }) {
  const { data, isLoading, isError, error } = usePrintData(id);

  return match({ isLoading, isError, error })
    .with({ isLoading: true }, () => <div>Loading...</div>)
    .with({ isError: true, error: P.when(is404Error) }, () => (
      <ErrorDisplay error="The requested data could not be found." />
    ))
    .with({ isError: true, error: P.when(isDecodingError) }, () => <ErrorDisplay error="Invalid Data" />)
    .with({ isError: true }, () => (
      <ErrorDisplay error="Failed to load data due to an unexpected error. Please try again later." />
    ))
    .otherwise(() =>
      match(data)
        .with(undefined, () => <ErrorDisplay error="An error occurred loading the data" />)
        .otherwise((data) => (
          <VStack alignItems={"start"} padding={4} className="printContent">
            <PrintControls />
            <PrintDataView data={data} />
          </VStack>
        )),
    );
}

export function is404Error(error: unknown) {
  const decodedError = object({ response: object({ status: number }) }).value(error);

  return decodedError?.response?.status === 404;
}

export function isDecodingError(error: unknown) {
  return error instanceof Error && error.name.includes("Decoding error");
}

export function projectName(projectId: ProjectID) {
  return match(projectId)
    .with("surfaceWater", () => "Surface Water")
    .with("groundWater", () => "Ground Water")
    .with("drinkingWater", () => "Drinking Water")
    .exhaustive();
}

function ErrorDisplay({ error }: { error: string }) {
  return (
    <VStack alignItems={"start"} padding={4}>
      <PrintControls showPrintButton={false} />
      <p>{error}</p>
    </VStack>
  );
}

function PrintDataView({ data }: { data: PrintData }) {
  return match(data)
    .with({ type: "dashboardData" }, (data) => <PrintDashboardDataView data={data} />)
    .with({ type: "wellLevelsData" }, (data) => <PrintWellLevelsDataView data={data} />)
    .exhaustive();
}

function PrintDashboardDataView({ data }: { data: PrintDashboardData }) {
  return (
    <>
      <h1>
        {projectName(data.project)} ({data.analyte})
      </h1>
      <h1>
        {data.startDate.toLocaleDateString()} - {data.endDate.toLocaleDateString()}
      </h1>
      <HStack>
        <h1>Sites: </h1>
        <span>{data.sites.join(", ")}</span>
      </HStack>
      <table className="printTable">
        <thead>
          <tr>
            <th>Site</th>
            <th>Sample Date</th>
            <th>Measurement Result</th>
          </tr>
        </thead>
        <tbody>
          {data.samples.map((sample, index) => (
            <tr key={index}>
              <td>{sample.site}</td>
              <td>{sample.sampleDate.toLocaleDateString()}</td>
              <td>{sample.measurementResult}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function PrintWellLevelsDataView({ data }: { data: PrintWellLevelsData }) {
  const sites: (keyof typeof wellLevelsSitesReference)[] = ["ns2", "ns3", "ns4", "pc2", "pc3", "pc4", "pc5"];

  return (
    <>
      <h1>Groundwater Well Levels</h1>
      <h1>
        {data.startDate.toLocaleDateString()} - {data.endDate.toLocaleDateString()}
      </h1>
      <HStack>
        <h1>Sites: </h1>
        <span>{data.sites.join(", ")}</span>
      </HStack>
      <h1>Access DB Data:</h1>
      <table className="printTable">
        <thead>
          <tr>
            <th>Date</th>
            {sites.map((site) => (
              <SiteHeader key={site} site={site} sites={data.sites} />
            ))}
            <th>Rainfall</th>
          </tr>
        </thead>
        <tbody>
          {data.wellLevels.map((entry, index) => (
            <tr key={index}>
              <td>{entry.date}</td>
              {sites.map((site) => (
                <SiteCell key={site} site={site} sites={data.sites} entry={entry} />
              ))}
              <td>{entry.rainfall}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h1>SCADA Data:</h1>
      <table className="printTable">
        <thead>
          <tr>
            <th>Well Name</th>
            <th>Date</th>
            <th>Water Level (FT)</th>
            <th>Pump Status</th>
          </tr>
        </thead>
        <tbody>
          {data.scadaWellLevels.map((entry, index) => (
            <tr key={index}>
              <td>{entry.wellName}</td>
              <td>{entry.date.toLocaleString()}</td>
              <td>{entry.waterLevelFT}</td>
              <td>{entry.pumpStatus}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ShowIfContains({ site, sites, children }: { site: string; sites: string[]; children: React.ReactNode }) {
  return sites.includes(site) ? <>{children}</> : null;
}

function SiteHeader({ site, sites }: { site: keyof typeof wellLevelsSitesReference; sites: string[] }) {
  return (
    <ShowIfContains site={site} sites={sites}>
      <th>{wellLevelsSitesReference[site].label}</th>
    </ShowIfContains>
  );
}

function SiteCell({
  site,
  sites,
  entry,
}: {
  site: keyof typeof wellLevelsSitesReference;
  sites: string[];
  entry: any;
}) {
  return (
    <ShowIfContains site={site} sites={sites}>
      <td>{entry[site]}</td>
    </ShowIfContains>
  );
}
